/* eslint-disable react/prop-types */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useQuery } from '@apollo/client';

import { C, F } from '../constants/theme';
import { Btn, Callout, Heading, IconBox, Radio, Sub } from '../components';
import { programList } from '../helpers';
import { GET_USER_BLOODWORK_MARKERS } from 'core/api/apollo/queries';
// import useListPrograms from 'core/hooks/useListPrograms';
import { useProgramContext } from 'core/api/programContext';
import useListPrograms from 'core/hooks/useListPrograms';

const Phase5Program = ({
  subStep,
  bioOption,
  goPhase,
}) => {
  const { selectedProgramSizeOptions, filterMealsByProgram } = useListPrograms();
  const {
    selectedProgram,
    setSelectedProgram,
    programSize,
    setProgramSize,
    selectedDiet,
    // sizeLabel,
    flagCount,
    getRecCopy,
    getRecommendedProgram,
    getRecommendedSize,
  } = useProgramContext();

  const { data: { listUserBloodworkMarkers = [] } = {} } = useQuery(GET_USER_BLOODWORK_MARKERS, {
    fetchPolicy: 'network-only',
  });

  // eslint-disable-next-line no-console
  console.log('selectedProgram', selectedProgram);
  // eslint-disable-next-line no-console
  console.log('programSize', programSize);
  // eslint-disable-next-line no-console
  console.log('selectedProgramSizeOptions', selectedProgramSizeOptions);
  // eslint-disable-next-line no-console
  console.log('filterMealsByProgram', filterMealsByProgram);
  // eslint-disable-next-line no-console
  console.log('selectedDiet', selectedDiet);

  const mealPortionSizeData = {
    'Standard': '35g+ protein per lunch/dinner meal · 350-475 cal per meal',
    'Large': selectedProgram === 'peak' ?
      '45g+ protein per meal · 500-700 cal · 30g+ complex carbs · ≤20g fat' :
      '45g+ protein per lunch/dinner meal · 500-700 cal per meal',
    'X-Large': '55g+ protein per meal · 700-900 cal · 35g+ complex carbs · ≤25g fat',
  };

  const progLabel = selectedProgram === 'sustain' ?
    'Sustain GLP-1' : selectedProgram === 'peak' ? 'Peak' : 'Signature';

  const recommendedProgramName = getRecommendedProgram();
  const recommendedSizeName =  getRecommendedSize(selectedProgram);

  return <>
    {subStep === 0 && <>
      <Heading>We'd recommend <span style={{ textTransform: 'capitalize' }}>{recommendedProgramName}</span> for you.</Heading>
      <Sub>Based on everything you've told us, this is the best fit. You can always switch.</Sub>

      {listUserBloodworkMarkers?.length > 0 && <Callout>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.darkBrown, marginBottom: 6 }}>
          {bioOption === 'flag' ? "What you've told us" : 'What your bloodwork tells us'}
        </div>
        {listUserBloodworkMarkers.map((marker) =>
          <div key={marker?.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 12 }}>{marker?.status === 'TOO_HIGH' ? '⬆️' : '⬇️'}</span>
            <span style={{ fontSize: 12, color: C.brown }}>
              {marker?.bloodworkMarker?.name} ({marker?.status.replace(/_/g, ' ')})
            </span>
          </div>
        )}
        <div style={{ fontSize: 11, color: C.brown, marginTop: 8 }}>
          Every meal in your plan will be weighted to help move {flagCount === 1 ? 'this' : 'these'} toward optimal.
        </div>
      </Callout>}

      <div style={{ background: C.optimalBg, border: `1.5px solid ${C.green}`, borderRadius: 0, padding: '14px 16px', marginBottom: 14 }}>
        <p style={{ fontSize: 13, color: C.brown, lineHeight: 1.55, margin: 0 }}>{getRecCopy(recommendedProgramName)}</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {programList.map((p) => {
          const isRec = getRecommendedProgram() === p.id;
          const active = selectedProgram === p.id;
          return <div key={p.id}
            onClick={() => { setSelectedProgram(p.id); setProgramSize(getRecommendedSize(p.id)); }}
            style={{
              padding: active ? '18px' : '14px 16px',
              background: C.pureWhite, border: `${active ? '2px' : '1px'} solid ${active ? C.darkBrown : '#E8E4DC'}`,
              borderRadius: 0,
              cursor: 'pointer',
              position: 'relative',
          }}>
            {isRec && <div style={{ position: 'absolute', top: -9, left: 16, fontSize: 9, fontWeight: 700, letterSpacing: '1px', color: C.pureWhite, background: C.darkBrown, padding: '2px 8px', borderRadius: 3 }}>RECOMMENDED</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: active ? 8 : 0 }}>
              <IconBox name={p.ic} active={active}/>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: active ? 16 : 14, fontWeight: 600, color: C.darkBrown }}>{p.l}</span>
                <span style={{ fontSize: 12, color: C.lightBrown, marginLeft: 8 }}>{p.spec}</span>
              </div>
              <Radio active={active}/>
            </div>
            {active && <p style={{ fontSize: 12, color: C.brown, lineHeight: 1.5, margin: '4px 0 0 48px' }}>{p.d}</p>}
          </div>;
        })}
      </div>
      <div style={{ fontSize: 11, color: C.lightBrown, textAlign: 'center', marginBottom: 12 }}>
        Not quite right? Tap any card to switch.
      </div>
      <Btn disabled={!selectedProgram} onClick={() => goPhase(5, 1)} style={{ width: '100%' }}>
        Continue <ChevronRight size={15}/>
      </Btn>
    </>}

    {subStep === 1 && <>
      <Heading>We'd suggest <span style={{ textTransform: 'capitalize' }}>{recommendedSizeName}</span> for you.</Heading>
      <Sub>Recommended based on your activity level and goals.</Sub>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {selectedProgramSizeOptions && selectedProgramSizeOptions.map((s) => {
          const isRec = getRecommendedSize(selectedProgram)?.toLowerCase() === s.name?.toLowerCase();
          const active = programSize?.id === s?.id;
          return <div key={s.id} onClick={() => setProgramSize(s)}
            style={{ flex: 1, padding: 16, background: C.pureWhite,
              border: `${active ? '2px' : '1px'} solid ${active ? C.darkBrown : '#E8E4DC'}`,
              borderRadius: 0, cursor: 'pointer', position: 'relative', textAlign: 'center',
            }}>
            {isRec && <div style={{ position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', fontSize: 8, fontWeight: 700, letterSpacing: '0.8px', color: C.pureWhite, background: C.darkBrown, padding: '2px 8px', borderRadius: 3, whiteSpace: 'nowrap' }}>RECOMMENDED</div>
            }
            <div style={{ fontFamily: F.h, fontSize: 20, fontWeight: 500, color: C.darkBrown, marginBottom: 6 }}>{s.name}</div>
            <div style={{ fontSize: 11, color: C.brown, lineHeight: 1.5 }}>{mealPortionSizeData[s.name]}</div>
          </div>;
        })}
      </div>
      <div style={{ fontSize: 11, color: C.lightBrown, textAlign: 'center', marginBottom: 12 }}>
        Not sure? You can change anytime.
      </div>
      <Btn
        onClick={() => goPhase(6)}
        disabled={!programSize?.name}
        style={{ width: '100%' }}>Continue with {progLabel} · {programSize?.name} <ChevronRight size={15}/>
      </Btn>
    </>}
  </>;
};

export default Phase5Program;
